import { useCallback, useMemo, useRef } from 'react'

import { useEvent } from '../useEvent'

type Callback<T extends Element> = (contentRect: DOMRectReadOnly, element: T) => void

/**
 * Wrapper for `ResizeObserver`. Returns a ref for the element to be observed.
 *
 * @example
 * ```ts
 * const Observer = () => {
 * 	const ref = useSizeEffect((contentRect) => console.log(contentRect.width))
 * 	return <div ref={ref} />
 * }
 * ```
 */
export const useSizeEffect = <T extends Element>(callback: Callback<T>) => {
	const persistedCallback = useEvent(callback)

	const animationFrameRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null)
	const observer = useMemo(
		() =>
			new ResizeObserver(([{ contentRect, target }]) => {
				if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
				animationFrameRef.current = requestAnimationFrame(() => {
					animationFrameRef.current = null
					persistedCallback(contentRect as DOMRectReadOnly, target as T)
				})
			}),
		[persistedCallback],
	)
	const ref = useCallback(
		(element: T | null) => {
			if (element === null) return // Only necessary to satisfy typescript, ref will never be null in practice

			observer.observe(element)

			return () => {
				observer.unobserve(element)
			}
		},
		[observer],
	)

	return ref
}
